import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { ExpenseCardService } from '../../../services/expenseCard.service';
import { AccountService } from '../../../services/account.service';
import {
    CardImportReport, CardStatementDetail, CardStatementDTO, CorporateCardDTO,
} from '../../../interfaces/expense.interface';
import { Account } from '../../../interfaces/accounting.interface';
import SearchableSelect from '../../common/SearchableSelect';
import { AccountAutocomplete } from '../../common/AccountAutocomplete';
import { useToast } from '../../../context/ToastContext';
import { usePermission } from '../../../hooks/usePermission';
import { PERMISSIONS } from '../../../constants/permissions';
import { formatAmount } from '../../../utils/expenseDisplay';
import { toLocalIsoDate } from '../../../utils/dateUtils';
import { depositAccountOptions } from '../../../utils/postingAccounts';

const cell = (basis: number, max: number) => ({ flex: `1 1 ${basis}px`, maxWidth: max, minWidth: 0 });

/** Card statements: CSV import (preview then commit), matching status, pay, and writing personal spend to a receivable. */
const CardStatements: React.FC = () => {
    const toast = useToast();
    const canPay = usePermission(PERMISSIONS.EXPENSE_CLAIM_PAY);
    const fileRef = useRef<HTMLInputElement>(null);
    const [cards, setCards] = useState<CorporateCardDTO[]>([]);
    const [statements, setStatements] = useState<CardStatementDTO[]>([]);
    const [bankAccounts, setBankAccounts] = useState<Account[]>([]);
    const [cardId, setCardId] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [report, setReport] = useState<CardImportReport | null>(null);
    const [detail, setDetail] = useState<CardStatementDetail | null>(null);
    const [payTarget, setPayTarget] = useState<CardStatementDTO | null>(null);
    const [payAccount, setPayAccount] = useState('');
    const [payDate, setPayDate] = useState(toLocalIsoDate());

    const reload = useCallback(async () => {
        try {
            const [c, s] = await Promise.all([ExpenseCardService.listCards(), ExpenseCardService.listStatements()]);
            setCards(c); setStatements(s);
        } catch (e: any) {
            toast.showApiError(e, 'Failed to load card statements');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        reload();
        AccountService.getAccountsByType('ASSET').then(a => setBankAccounts(depositAccountOptions(a))).catch(() => undefined);
    }, [reload]);

    const preview = async () => {
        try {
            setReport(await ExpenseCardService.previewImport(cardId, file!));
        } catch (e: any) {
            toast.showApiError(e, 'Could not read the file');
        }
    };

    const commit = async () => {
        try {
            const res = await ExpenseCardService.commitImport(cardId, file!);
            toast.showSuccess(`Imported ${res.rowCount} transactions`);
            setReport(null); setFile(null);
            if (fileRef.current) fileRef.current.value = '';
            await reload();
        } catch (e: any) {
            toast.showApiError(e, 'Import failed');
        }
    };

    const open = async (s: CardStatementDTO) => {
        try { setDetail(await ExpenseCardService.getStatement(s.id)); } catch (e: any) { toast.showApiError(e, 'Failed to load statement'); }
    };

    const pay = async () => {
        try {
            await ExpenseCardService.payStatement(payTarget!.id, payAccount, payDate);
            toast.showSuccess('Statement paid');
            setPayTarget(null);
            await reload();
        } catch (e: any) {
            toast.showApiError(e, 'Payment failed');
        }
    };

    const writeOff = async (txnId: string) => {
        try {
            await ExpenseCardService.writeToReceivable(txnId);
            toast.showSuccess('Written to the employee receivable');
            if (detail) setDetail(await ExpenseCardService.getStatement(detail.statement.id));
            await reload();
        } catch (e: any) {
            toast.showApiError(e, 'Could not write to receivable');
        }
    };

    return (
        <Box sx={{ p: { xs: 1.5, sm: 3 } }} data-help-page="expense.statements.page">
            <Typography variant="h5" sx={{ mb: 0.5 }}>Card statements</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                CSV columns: date, description, amount, reference. Nothing is saved until you confirm the preview.
            </Typography>
            {canPay && (
                <Paper sx={{ p: 2, mb: 2 }} data-help-section="expense.statements.import">
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                        <SearchableSelect<CorporateCardDTO> data-help="expense.statements.card" options={cards.filter(c => c.isActive)} value={cardId}
                            onChange={id => { setCardId(id); setReport(null); }} getOptionLabel={c => `${c.label} - ${c.holderName ?? ''}`}
                            label="Card" size="small" sx={cell(220, 340)} />
                        <Button variant="outlined" component="label" data-help="expense.statements.chooseCsv">
                            {file ? file.name : 'Choose CSV'}
                            <input ref={fileRef} hidden type="file" accept=".csv,text/csv" data-testid="statement-file"
                                   onChange={e => { setFile(e.target.files?.[0] ?? null); setReport(null); }} />
                        </Button>
                        <Button data-help="expense.statements.preview" variant="contained" disabled={!cardId || !file} onClick={preview}>Preview</Button>
                        <Button data-help="expense.statements.commit" variant="contained" color="success" disabled={!report || report.errorRows > 0 || report.batchErrors.length > 0} onClick={commit}>
                            Import {report ? report.validRows : ''} rows
                        </Button>
                    </Box>
                    {report && (
                        <Box sx={{ mt: 2 }} data-testid="import-report">
                            {report.batchErrors.map((b, i) => <Alert key={i} severity="error" sx={{ mb: 1 }}>{b}</Alert>)}
                            <Typography variant="body2">{report.validRows} valid, {report.errorRows} with errors of {report.totalRows} rows.</Typography>
                            {report.rows.filter(r => !r.valid).map(r => (
                                <Alert key={r.rowNumber} severity="warning" sx={{ mt: 0.5 }}>Row {r.rowNumber}: {r.errors.join(', ')}</Alert>
                            ))}
                            {report.rows.length > 0 && (
                                <TableContainer sx={{ mt: 1.5, maxHeight: 360 }} data-testid="import-preview-rows" data-help-section="expense.statements.previewTable">
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell data-help="expense.statements.pcol.row">Row</TableCell><TableCell data-help="expense.statements.pcol.date">Date</TableCell><TableCell data-help="expense.statements.pcol.description">Merchant / description</TableCell>
                                                <TableCell align="right" data-help="expense.statements.pcol.amount">Amount</TableCell><TableCell data-help="expense.statements.pcol.reference">Reference</TableCell><TableCell data-help="expense.statements.pcol.onImport">On import</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {report.rows.map(r => (
                                                <TableRow key={r.rowNumber}>
                                                    <TableCell data-help="expense.statements.pcol.row">{r.rowNumber}</TableCell>
                                                    <TableCell data-help="expense.statements.pcol.date">{r.txnDate ?? ''}</TableCell>
                                                    <TableCell data-help="expense.statements.pcol.description">{r.description ?? ''}</TableCell>
                                                    <TableCell align="right" data-help="expense.statements.pcol.amount">{r.amount != null ? formatAmount(r.amount) : ''}</TableCell>
                                                    <TableCell data-help="expense.statements.pcol.reference">{r.reference ?? ''}</TableCell>
                                                    <TableCell data-help="expense.statements.pcol.onImport">
                                                        {r.valid
                                                            ? <Chip size="small" label="Unmatched until claimed" />
                                                            : <Chip size="small" color="warning" label={r.errors.join(', ')} />}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>
                    )}
                </Paper>
            )}
            <TableContainer component={Paper} data-help-section="expense.statements.table">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="expense.statements.col.card">Card</TableCell><TableCell data-help="expense.statements.col.period">Period</TableCell><TableCell align="right" data-help="expense.statements.col.total">Total</TableCell>
                            <TableCell data-help="expense.statements.col.counts">Matched / Personal / Unmatched</TableCell><TableCell data-help="expense.statements.col.status">Status</TableCell><TableCell data-help="expense.statements.col.actions" />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {statements.length === 0 && <TableRow><TableCell colSpan={6}>No statements imported yet.</TableCell></TableRow>}
                        {statements.map(s => (
                            <TableRow key={s.id} hover>
                                <TableCell data-help="expense.statements.col.card">{s.cardLabel}{s.holderName ? ` - ${s.holderName}` : ''}</TableCell>
                                <TableCell data-help="expense.statements.col.period">{s.periodFrom} to {s.periodTo}</TableCell>
                                <TableCell align="right" data-help="expense.statements.col.total">{formatAmount(s.totalAmount)}</TableCell>
                                <TableCell data-help="expense.statements.col.counts">{s.matchedCount} / {s.personalCount} / {s.unmatchedCount}</TableCell>
                                <TableCell data-help="expense.statements.col.status"><Chip size="small" label={s.status} color={s.status === 'PAID' ? 'success' : 'warning'} /></TableCell>
                                <TableCell align="right">
                                    <Button size="small" data-help="expense.statements.action.view" onClick={() => open(s)}>View</Button>
                                    {canPay && s.status === 'IMPORTED' && <Button size="small" data-help="expense.statements.action.pay" onClick={() => setPayTarget(s)}>Pay</Button>}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={Boolean(detail)} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
                <DialogTitle>{detail?.statement.cardLabel} {detail?.statement.periodFrom} to {detail?.statement.periodTo}</DialogTitle>
                <DialogContent>
                    <TableContainer data-help-section="expense.statements.detailTable">
                        <Table size="small">
                            <TableHead><TableRow><TableCell data-help="expense.statements.dcol.date">Date</TableCell><TableCell data-help="expense.statements.dcol.description">Description</TableCell><TableCell align="right" data-help="expense.statements.dcol.amount">Amount</TableCell><TableCell data-help="expense.statements.dcol.status">Status</TableCell><TableCell data-help="expense.statements.dcol.actions" /></TableRow></TableHead>
                            <TableBody>
                                {detail?.transactions.map(t => (
                                    <TableRow key={t.id}>
                                        <TableCell data-help="expense.statements.dcol.date">{t.txnDate}</TableCell><TableCell data-help="expense.statements.dcol.description">{t.description ?? t.reference}</TableCell>
                                        <TableCell align="right" data-help="expense.statements.dcol.amount">{formatAmount(t.amount)}</TableCell><TableCell data-help="expense.statements.dcol.status">{t.status}</TableCell>
                                        <TableCell align="right">
                                            {canPay && t.status === 'PERSONAL' && <Button size="small" data-help="expense.statements.action.writeToReceivable" onClick={() => writeOff(t.id)}>Write to receivable</Button>}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions><Button onClick={() => setDetail(null)}>Close</Button></DialogActions>
            </Dialog>

            <Dialog open={Boolean(payTarget)} onClose={() => setPayTarget(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Pay statement</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                    <Typography variant="body2">
                        {payTarget ? `${formatAmount(payTarget.totalAmount)} for ${payTarget.cardLabel}` : ''}. Unmatched items stay on the card liability until claimed or written to a receivable.
                    </Typography>
                    <AccountAutocomplete data-help="expense.statements.payAccount" accounts={bankAccounts} value={payAccount} onChange={setPayAccount} label="Pay from (cash/bank)" />
                    <TextField data-help="expense.statements.payDate" type="date" size="small" label="Payment date" value={payDate} InputLabelProps={{ shrink: true }}
                               onChange={e => setPayDate(e.target.value)} />
                </DialogContent>
                <DialogActions>
                    <Button data-help="expense.statements.payCancel" onClick={() => setPayTarget(null)}>Cancel</Button>
                    <Button data-help="expense.statements.payConfirm" variant="contained" disabled={!payAccount} onClick={pay}>Pay</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CardStatements;
